import { IChatMessage } from "@components/chatMessage/ChatMessage.utils";
import ChatMessageStream from "@components/chatMessage/ChatMessageStream";
import { TEntityKey } from "@odata/BindingContext";
import { IChatEntity } from "@odata/GeneratedEntityTypes";
import { WebSocketMessageTypeCode } from "@odata/GeneratedEnums";
import { TWebsocketMessage } from "@utils/websocketManager/Websocket.types";
import React from "react";

import TestIds from "../../testIds";
import WebsocketManager from "../../utils/websocketManager/WebsocketManager";
import { FormStorage } from "../../views/formView/FormStorage";
import { currentUserIsCustomer } from "../admin/users/Users.utils";
import { checkChatMessageNotification, getStoredMessages, isMessageMine } from "./Chats.utils";

interface IProps {
    storage: FormStorage<IChatEntity>;
    onCreateMessage: (message: IChatMessage, files: File[]) => Promise<boolean>;
    isReadOnly?: boolean;
}

interface IState {
    id?: TEntityKey;
    messages: IChatMessage[];
}

export default class SmartChatMessageStream extends React.Component<IProps, IState> {
    state: IState = {
        messages: []
    };

    _cancelWebsocketSubscription: () => void;

    componentDidMount() {
        this.setMessagesFromStorage();

        this._cancelWebsocketSubscription = WebsocketManager.subscribe({
            callback: this.handleTicketNotification,
            types: [WebSocketMessageTypeCode.Notification]
        });
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>) {
        const { entity, bindingContext } = this.props.storage.data;
        const entityHasChanged = bindingContext.getKey() !== this.state.id && !this.props.storage.loading;
        const messageshasBeenUpdated = (entity.Messages?.length || this.state.messages.length) && entity.Messages?.length !== this.state.messages.length;
        if (messageshasBeenUpdated || entityHasChanged) {
            this.setMessagesFromStorage();
        }
    }

    componentWillUnmount() {
        this._cancelWebsocketSubscription();
    }

    handleTicketNotification = async (message: TWebsocketMessage): Promise<void> => {
        const { storage } = this.props;
        const {
            isChatMessage,
            chatId
        } = await checkChatMessageNotification(message, storage.oData);
        if (isChatMessage && chatId === this.state.id) {
            await storage.reload({ preserveInfos: true, withoutBusy: true });
        }
    };

    handleCreate = async (message: IChatMessage, files: File[]): Promise<boolean> => {
        return !!(await this.props.onCreateMessage(message, files));
    };

    setMessagesFromStorage(): void {
        const { storage } = this.props;
        const isMine = isMessageMine(storage.context.getData().userSettings, storage.data.entity);

        this.setState({
            messages: getStoredMessages(storage, isMine),
            id: storage.data.bindingContext.getKey()
        });
    }

    render() {
        const { storage } = this.props;
        const _currentUserIsCustomer = currentUserIsCustomer(storage.context);

        return (
            <ChatMessageStream messages={this.state.messages}
                               onCreateMessage={this.handleCreate}
                               isCustomer={_currentUserIsCustomer}
                               isReadOnly={this.props.isReadOnly}
                               data-testid={TestIds.ChatMessageStream}/>
        );
    }
}
